import * as THREE from 'three';
import { LoadManager, LoadInformation } from '@/services/Loaders';
import { exportGlb } from '@/services/Exporters';

export type ConvertedUploadModel = {
  modelUrl: string;
  nonGltfModel: THREE.Object3D | null;
  /** GLB bytes that should be posted to the API (native GLB file or CAD export). */
  preparedGlb: Blob;
};

/** Per-file conversion budget (serial queue); IFC / CAD can take several minutes. */
export function conversionTimeoutMsForFile(fileName: string): number {
  const fileExtension = LoadInformation.getFileExtension(fileName).toLocaleLowerCase();
  return fileExtension === '.ifc' ? 300000 : 120000;
}

/**
 * Outer wait for the serial conversion queue: sum of remaining per-file budgets + buffer.
 */
export function maxConversionWaitMs(
  drafts: Array<{ file: { name: string }; status: string }>
): number {
  const busy = drafts.filter((d) => d.status === 'queued' || d.status === 'converting');
  if (busy.length === 0) return 0;
  const sum = busy.reduce((total, d) => total + conversionTimeoutMsForFile(d.file.name), 0);
  return sum + 5000;
}

/** Release GPU resources from a converted CAD tree after GLB export. */
export function disposeObject3D(root: THREE.Object3D | null | undefined): void {
  if (!root) return;
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    obj.geometry?.dispose();
    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (const mat of materials) {
      if (!mat) continue;
      for (const key of [
        'map',
        'normalMap',
        'roughnessMap',
        'metalnessMap',
        'emissiveMap',
        'aoMap',
        'alphaMap',
        'bumpMap',
        'displacementMap',
        'envMap',
      ] as const) {
        const tex = (mat as THREE.MeshStandardMaterial)[key as 'map'];
        if (tex && typeof tex.dispose === 'function') tex.dispose();
      }
      mat.dispose();
    }
  });
}

function loadModelAsObject3D(file: File): Promise<THREE.Object3D> {
  const timeoutMs = conversionTimeoutMsForFile(file.name);

  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      settled = true;
      reject(new Error('Model conversion timed out'));
    }, timeoutMs);

    try {
      LoadManager.loadModel(
        new LoadInformation(file),
        (model) => {
          window.clearTimeout(timer);
          if (settled) {
            disposeObject3D(model);
            return;
          }
          settled = true;
          resolve(model);
        },
        undefined,
        (error) => {
          window.clearTimeout(timer);
          if (settled) return;
          settled = true;
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      );
    } catch (error) {
      window.clearTimeout(timer);
      if (settled) return;
      settled = true;
      reject(error);
    }
  });
}

/**
 * Convert an uploaded model file the same way `useModelUpload` does, but as a promise
 * so batch create can prepare several files sequentially.
 */
export async function convertUploadedModel(file: File): Promise<ConvertedUploadModel> {
  const fileExtension = LoadInformation.getFileExtension(file.name).toLocaleLowerCase();

  if (fileExtension === '.glb' || fileExtension === '.gltf') {
    return {
      modelUrl: URL.createObjectURL(file),
      nonGltfModel: null,
      preparedGlb: file,
    };
  }

  const model = await loadModelAsObject3D(file);
  const blob = await exportGlb(model, 'model.glb');
  if (!blob) {
    disposeObject3D(model);
    throw new Error('GLB export failed');
  }

  return {
    modelUrl: URL.createObjectURL(blob),
    nonGltfModel: model,
    preparedGlb: blob,
  };
}
